import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/crm/Badges";
import { GhlStatusPanel } from "@/components/crm/ghl/GhlStatusPanel";
import { GhlFailedTables, GhlLogsTable } from "@/components/crm/ghl/GhlTables";
import { GhlEventsPanel } from "@/components/crm/ghl/GhlEventsPanel";
import { TestLeadsCleanupPanel } from "@/components/crm/ghl/TestLeadsCleanupPanel";
import { WebWelcomePanel } from "@/components/crm/ghl/WebWelcomePanel";

const BADGE = { Conectado: "border-emerald-200 bg-emerald-50 text-emerald-700", Error: "border-rose-200 bg-rose-50 text-rose-700" };

export default function GhlIntegrationPage() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [security, setSecurity] = useState([]);
  const [events, setEvents] = useState([]);
  const [failed, setFailed] = useState({ events: [], queue: [], messages: [] });

  const loadLists = useCallback(() => {
    api.get("/admin/integrations/ghl/logs").then((r) => setLogs(r.data.filter((l) => l.category !== "seguridad")));
    api.get("/admin/integrations/ghl/logs", { params: { category: "seguridad" } }).then((r) => setSecurity(r.data));
    api.get("/admin/integrations/ghl/events").then((r) => setEvents(r.data));
    api.get("/admin/integrations/ghl/failed").then((r) => setFailed(r.data));
  }, []);

  useEffect(() => {
    api.get("/admin/integrations/ghl/status").then((r) => setStatus(r.data)).catch((e) => toast.error(errorMessage(e)));
    loadLists();
    const install = params.get("install");
    if (install === "ok") toast.success("App de la plataforma de mensajería instalada correctamente");
    if (install === "error") toast.error(params.get("motivo") || "No se pudo instalar la app");
    if (install) setParams({}, { replace: true });
  }, [loadLists]); // eslint-disable-line react-hooks/exhaustive-deps

  const retry = async (kind, id) => {
    try {
      await api.post(`/admin/integrations/ghl/retry/${kind}/${id}`);
      toast.success("Reintento ejecutado");
    } catch (e) { toast.error(errorMessage(e)); }
    loadLists();
  };

  if (!status) return <Loader2 className="h-6 w-6 animate-spin text-[#1E6FD9]" />;
  const failedCount = failed.events.length + failed.queue.length + failed.messages.length;

  return (
    <div data-testid="integrations-page">
      <PageHeader title="Integraciones" subtitle="Plataforma de mensajería">
        <span className={`inline-flex rounded-full border px-3 py-1.5 text-xs font-semibold ${BADGE[status.state] || "border-slate-200 bg-white text-slate-600"}`}
              data-testid="ghl-state-badge">{status.state}</span>
      </PageHeader>
      <Tabs defaultValue="estado">
        <TabsList className="mb-5 h-auto flex-wrap justify-start rounded-xl bg-white p-1">
          <TabsTrigger value="estado" className="rounded-lg" data-testid="ghl-tab-status">Estado</TabsTrigger>
          <TabsTrigger value="logs" className="rounded-lg" data-testid="ghl-tab-logs">Registros</TabsTrigger>
          <TabsTrigger value="seguridad" className="rounded-lg" data-testid="ghl-tab-security">Seguridad ({security.length})</TabsTrigger>
          <TabsTrigger value="fallidos" className="rounded-lg" data-testid="ghl-tab-failed">Fallidos ({failedCount})</TabsTrigger>
          <TabsTrigger value="eventos" className="rounded-lg" data-testid="ghl-tab-events">Webhooks ({events.length})</TabsTrigger>
          <TabsTrigger value="bienvenidas" className="rounded-lg" data-testid="ghl-tab-welcome">Bienvenidas web</TabsTrigger>
          <TabsTrigger value="limpieza" className="rounded-lg" data-testid="ghl-tab-cleanup">Limpiar leads de prueba</TabsTrigger>
        </TabsList>
        <TabsContent value="estado"><GhlStatusPanel status={status} onChange={(s) => { setStatus(s); loadLists(); }} /></TabsContent>
        <TabsContent value="logs"><GhlLogsTable logs={logs} testid="ghl-logs-table" /></TabsContent>
        <TabsContent value="seguridad"><GhlLogsTable logs={security} testid="ghl-security-table" /></TabsContent>
        <TabsContent value="fallidos"><GhlFailedTables failed={failed} onRetry={retry} /></TabsContent>
        <TabsContent value="eventos"><GhlEventsPanel reloadKey={events} /></TabsContent>
        <TabsContent value="bienvenidas"><WebWelcomePanel /></TabsContent>
        <TabsContent value="limpieza"><TestLeadsCleanupPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
